import { useState, useEffect, useMemo } from 'react';
import { Bell, CheckCircle, TrendingUp, Target, Trophy, Check, Mail, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import {
  getNotifications,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
  deleteNotification,
} from '@/services/api/notifications.api';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Badge } from '@/components/atoms/Badge';
import { cn } from '@/utils/cn';
import type { Notification } from '@/types/gamification.types';
import styles from './NotificationsPage.module.scss';

// TODO Phase 6+: Implement NotificationContext, API integration

function getNotificationIcon(type: Notification['type']) {
  switch (type) {
    case 'achievement_unlocked':
      return Trophy;
    case 'level_up':
    case 'rank_change':
      return TrendingUp;
    case 'streak_milestone':
      return CheckCircle;
    case 'story_assigned':
    case 'story_reminder':
      return Target;
    case 'system':
    default:
      return Bell;
  }
}

function getRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
  if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
  if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  return date.toLocaleDateString('es-ES');
}

export function NotificationsPage() {
  const { user } = useAuth();
  const { refreshCount } = useNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load notifications on mount
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getNotifications(user.id, { limit: 20 });
        setNotifications(data);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadNotifications();
  }, [user?.id]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: true } : notif))
      );
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAsUnread = async (id: string) => {
    try {
      await markNotificationAsUnread(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, isRead: false } : notif))
      );
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((notif) => notif.id !== id));
      void refreshCount();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await markAllNotificationsAsRead(user.id);
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
      void refreshCount();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  return (
    <div className={styles.notifications}>
      <div className={styles.notifications__header}>
        <div>
          <h1 className={styles.notifications__title}>
            Notificaciones
            {unreadCount > 0 && (
              <Badge variant="secondary" className={styles.notifications__badge}>
                {unreadCount}
              </Badge>
            )}
          </h1>
          <p className={styles.notifications__subtitle}>Alertas del sistema y actividad de la plataforma</p>
        </div>

        {notifications.length > 0 && (
          <div className={styles.notifications__actions}>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
                Marcar todo como leído
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
              Limpiar todo
            </Button>
          </div>
        )}
      </div>

      <div className={styles.notifications__list}>
        {isLoading ? (
          <div className={styles.notifications__loading}>Cargando notificaciones...</div>
        ) : notifications.length === 0 ? (
          <div className={styles.notifications__empty}>
            <Icon icon={Bell} size="lg" />
            <p className={styles.notifications__emptyText}>Sin notificaciones</p>
            <p className={styles.notifications__emptySubtext}>
              Te avisaremos cuando haya actividad importante en el sistema
            </p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={cn(styles.notifications__item, !notification.isRead && styles['notifications__item--unread'])}
            >
              <div className={styles.notifications__itemIcon}>
                <Icon icon={getNotificationIcon(notification.type)} size="md" />
              </div>

              <div className={styles.notifications__itemContent}>
                <h3 className={styles.notifications__itemTitle}>{notification.title}</h3>
                <p className={styles.notifications__itemMessage}>{notification.message}</p>
                <p className={styles.notifications__itemTimestamp}>
                  {getRelativeTime(notification.createdAt)}
                </p>
              </div>

              <div className={styles.notifications__itemActions}>
                {notification.isRead ? (
                  <button
                    className={styles.notifications__actionButton}
                    onClick={() => handleMarkAsUnread(notification.id)}
                    title="Marcar como no leído"
                  >
                    <Icon icon={Check} size="sm" />
                  </button>
                ) : (
                  <button
                    className={styles.notifications__actionButton}
                    onClick={() => handleMarkAsRead(notification.id)}
                    title="Marcar como leído"
                  >
                    <Icon icon={Mail} size="sm" />
                  </button>
                )}
                <button
                  className={cn(styles.notifications__actionButton, styles['notifications__actionButton--danger'])}
                  onClick={() => handleDelete(notification.id)}
                  title="Eliminar"
                >
                  <Icon icon={Trash2} size="sm" />
                </button>
              </div>

              {!notification.isRead && <div className={styles.notifications__itemDot} />}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
